"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useImagePreloader } from "@/hooks/use-image-preloader";
import { cn } from "@/lib/utils";
import { getCurrentAreaInClient } from "@/lib/utils.client";
import { useORPC } from "@/providers/orpc-provider";
import { CMSImage, CMSLink } from "../components/cms-items";
import { getImageUrlByDevice } from "../utils";
import { advertisementRecordsAtom } from "./store/advertisement-atoms";
import { type AdvertisementConfig, shouldShowAdvertisement } from "./utils/advertisement-helpers";
export function AdvertisementRegistrationGuide(t0) {
  const $ = _c(21);
  const {
    className
  } = t0;
  const area = getCurrentAreaInClient();
  const orpc = useORPC();
  const {
    preload,
    statuses,
    loadedUrls
  } = useImagePreloader();
  const [, setAdvertisementRecords] = useAtom(advertisementRecordsAtom);
  const {
    data: advertisementsResponse
  } = useQuery({
    ...orpc.cms.content.public_findLiveWithPagination.queryOptions({
      input: {
        page: 1,
        pageSize: 10,
        area: area?.toUpperCase(),
        parentAlias: "advertisement-config",
        platform: "web"
      }
    }),
    staleTime: 300000
  });
  const advertisementConfig = (advertisementsResponse as any)?.items?.find(_temp) as AdvertisementConfig | undefined;
  const image = advertisementConfig?.data?.image;
  const shouldShow = shouldShowAdvertisement(advertisementConfig, "web", area);
  let t1;
  let t2;
  if ($[0] !== image || $[1] !== preload) {
    t1 = () => {
      if (!image) {
        return;
      }
      const imageUrl = getImageUrlByDevice(image);
      if (imageUrl) {
        preload("registration-guide", [imageUrl]);
      }
    };
    t2 = [image, preload];
    $[0] = image;
    $[1] = preload;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== advertisementConfig || $[5] !== area || $[6] !== setAdvertisementRecords || $[7] !== shouldShow) {
    t3 = () => {
      if (shouldShow && advertisementConfig) {
        const cacheKey = `${advertisementConfig.id}_${area}`;
        setAdvertisementRecords(prev => ({
          ...prev,
          [cacheKey]: {
            id: advertisementConfig.id,
            area,
            lastShown: Date.now()
          }
        }));
      }
    };
    t4 = [shouldShow, advertisementConfig, setAdvertisementRecords, area];
    $[4] = advertisementConfig;
    $[5] = area;
    $[6] = setAdvertisementRecords;
    $[7] = shouldShow;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  const isImageReady = statuses["registration-guide"] === "loaded" && !!loadedUrls["registration-guide"];
  if (!advertisementConfig || !shouldShow) {
    return null;
  }
  let t5;
  if ($[10] !== className) {
    t5 = cn("mx-auto w-full max-w-[444px]", className);
    $[10] = className;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = advertisementConfig.data?.link;
  let t7;
  if ($[12] !== advertisementConfig.data?.image || $[13] !== isImageReady) {
    t7 = isImageReady ? <CMSImage image={advertisementConfig.data?.image} width={444} height={124} className="aspect-444/124 object-contain" /> : <div className="flex aspect-444/124 h-auto w-full items-center justify-center" />;
    $[12] = advertisementConfig.data?.image;
    $[13] = isImageReady;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <CMSLink link={t6}>{t7}</CMSLink>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t5 || $[19] !== t8) {
    t9 = <div className={t5}>{t8}</div>;
    $[18] = t5;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
function _temp(item) {
  return item.type === "registration-guide-image";
}